/**
 * Submit Parking Flow
 * Allows users to submit a new parking location when none are found.
 */
import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

interface SubmitParkingFlowProps {
  lat: number;
  lng: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function SubmitParkingFlow({ lat, lng, onClose, onSuccess }: SubmitParkingFlowProps) {
  const { firebaseUser } = useAuth();
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firebaseUser) {
      setError('Please sign in to submit a parking');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await addDoc(collection(db, 'submissions'), {
        lat,
        lng,
        name: name.trim() || null,
        notes: notes.trim() || null,
        status: 'pending',
        submittedBy: firebaseUser.uid,
        submittedAt: serverTimestamp(),
      });
      onSuccess();
    } catch (err) {
      console.error('Failed to submit parking:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-5 border-b border-rule">
          <h2 className="text-lg font-semibold text-ink1">Submit New Parking</h2>
          <p className="text-sm text-ink3 mt-1">
            Help us improve coverage by adding a parking we missed.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-ink2 mb-1">Location</label>
            <div className="text-sm text-ink3 bg-tint px-3 py-2 rounded-lg">
              {lat.toFixed(6)}, {lng.toFixed(6)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink2 mb-1">
              Parking Name <span className="text-ink3">(optional)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., City Center Parking"
              className="w-full px-3 py-2 border border-rule rounded-lg text-sm text-ink1 placeholder-ink3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink2 mb-1">
              Notes <span className="text-ink3">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details (entrance location, height restrictions you know of, etc.)"
              rows={3}
              className="w-full px-3 py-2 border border-rule rounded-lg text-sm text-ink1 placeholder-ink3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2.5 border border-rule rounded-lg text-sm font-medium text-ink2 hover:bg-tint transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2.5 bg-ink1 text-card rounded-lg text-sm font-medium hover:bg-ink2 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>

          <p className="text-xs text-ink3 text-center">
            We'll review your submission and add it to the map if verified.
          </p>
        </form>
      </div>
    </div>
  );
}

interface SubmitParkingButtonProps {
  onStartSubmission: () => void;
}

export function SubmitParkingButton({ onStartSubmission }: SubmitParkingButtonProps) {
  return (
    <button
      onClick={onStartSubmission}
      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
    >
      + Add a parking here
    </button>
  );
}
